import React, { useEffect, useState } from "react";
import { Coins, MousePointerClick, ShoppingBag, TrendingUp, UserPlus, Wallet } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import Carregando from "../comum/Carregando";
import FiltroPeriodo from "../comum/FiltroPeriodo";
import GraficoDias from "../comum/GraficoDias";
import Kpi from "../comum/Kpi";
import { pct, reais } from "../comum/formato";
import { useFiltros } from "../comum/useFiltros";
import { rotaCampanhaMeta } from "../rotas";
import { carregarMetaAds, lucroPorReal, retornoX } from "./api";
import Funil from "./Funil";
import TabelaCampanhas from "./TabelaCampanhas";

// Relatórios → Anúncios Meta: quanto gastou (Meta) × quanto vendeu (Núcleo), por campanha.
export default function RelatorioMetaAdsPage() {
  const { filtros, mudar, query, paraApi } = useFiltros();
  const navigate = useNavigate();
  const [rel, setRel] = useState(null);
  // Erro ao carregar: aviso com "Tentar de novo" no lugar do carregamento sem fim.
  const [erro, setErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  const chave = JSON.stringify({ de: paraApi.date_from, ate: paraApi.date_to });

  useEffect(() => {
    let vivo = true;
    // Trocar período/filtro mantém o relatório na tela até o novo chegar (25/10/2026)
    setErro(false);
    carregarMetaAds({ date_from: paraApi.date_from, date_to: paraApi.date_to })
      .then((d) => { if (vivo) setRel(d); })
      .catch(() => { if (vivo) { setErro(true); toast.error("Não foi possível carregar o relatório de Anúncios Meta"); } });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave, tentativa]);

  const r = rel?.resumo || {};
  const a = rel?.anterior || {};
  return (
    <div className="p-4 sm:p-6">
      <div className="space-y-5" data-e2e="relatorio-meta-ads">
        {/* Cabeçalho no padrão dos relatórios (27/10/2026): relatório próprio do menu, sem "voltar" nem ícone */}
        <div>
          <h1 className="text-3xl font-bold">Relatório de Anúncios Meta</h1>
          <p className="text-muted-foreground mt-1">Quanto cada campanha gastou no Instagram e no Facebook, quantos pedidos trouxe e quanto sobrou depois do anúncio.</p>
        </div>
        <FiltroPeriodo de={filtros.de} ate={filtros.ate} onMudar={mudar} cor="bg-sky-500 text-white" />

        {!rel ? <Carregando erro={erro} onTentar={() => setTentativa((t) => t + 1)} /> : (
          <>
            {!rel.conectada && (
              <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
                A loja não está conectada à Meta: o gasto não aparece. Os pedidos que chegaram pelo link do anúncio continuam contados. Conecte em Impulsione → Anúncios Meta.
              </p>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi rotulo="Investido" valor={reais(r.investido)} atual={r.investido} anterior={a.investido} menorMelhor dica={`gasto ${reais(r.gasto)} + ${rel.imposto_pct}% de imposto`} icone={Wallet} e2e="kpi-investido" />
              <Kpi rotulo="Pedidos" valor={r.pedidos} atual={r.pedidos} anterior={a.pedidos} dica={r.cancelados ? `${r.cancelados} cancelado(s) fora` : "vindos do anúncio"} icone={ShoppingBag} e2e="kpi-pedidos" />
              <Kpi rotulo="Faturado" valor={reais(r.faturado)} atual={r.faturado} anterior={a.faturado} dica={`ticket ${reais(r.ticket_medio)}`} icone={Coins} destaque="bg-sky-500/10 border-sky-500/30" e2e="kpi-faturado" />
              <Kpi rotulo="ROI" valor={retornoX(r.retorno)} atual={r.retorno} anterior={a.retorno} dica="faturado ÷ investido" icone={TrendingUp} e2e="kpi-retorno" />
              <Kpi rotulo="Custo por pedido" valor={r.custo_por_pedido == null ? "—" : reais(r.custo_por_pedido)} atual={r.custo_por_pedido} anterior={a.custo_por_pedido} menorMelhor dica="investido ÷ pedidos" icone={MousePointerClick} />
              <Kpi rotulo="Lucro bruto" valor={r.lucro_bruto == null ? "—" : reais(r.lucro_bruto)} atual={r.lucro_bruto} anterior={a.lucro_bruto} dica={r.lucro_bruto == null ? "sem custo nos itens" : `custo em ${pct(r.cobertura_custo_pct)} dos pedidos`} icone={TrendingUp} />
              <Kpi rotulo="Lucro depois do anúncio" valor={r.lucro_depois_anuncio == null ? "—" : reais(r.lucro_depois_anuncio)} atual={r.lucro_depois_anuncio} anterior={a.lucro_depois_anuncio} dica={lucroPorReal(r.lucro_por_real_investido) || "lucro bruto − investido"} icone={TrendingUp} destaque="bg-emerald-500/10 border-emerald-500/30" e2e="kpi-lucro-anuncio" />
              <Kpi rotulo="Clientes novos" valor={r.clientes_novos} atual={r.clientes_novos} anterior={a.clientes_novos} dica={`de ${r.clientes} clientes`} icone={UserPlus} e2e="kpi-novos" />
            </div>
            <p className="text-[11px] text-muted-foreground -mt-2">
              Setas: comparação com os {rel.periodo.dias} dias anteriores. Pedidos e faturado são os que chegaram pelo link do anúncio (último clique em 7 dias; cancelado não conta).
              A Meta diz {r.meta_diz?.compras ?? 0} compra(s) ({reais(r.meta_diz?.valor)}) pelo Pixel — ela conta também quem só viu o anúncio.
              {r.itens_sem_custo > 0 && ` ${r.itens_sem_custo} item(ns) sem custo cadastrado: o lucro desses pedidos não entra.`}
            </p>
            <div className="grid lg:grid-cols-5 gap-4">
              <div className="lg:col-span-3">
                <GraficoDias serie={rel.por_dia} principal="faturado" secundaria="investido" titulo="Faturado × investido por dia"
                  legenda={[{ rotulo: "Faturado", cor: "bg-sky-500" }, { rotulo: "Investido", cor: "bg-amber-400" }]} />
              </div>
              <div className="lg:col-span-2"><Funil etapas={rel.funil} /></div>
            </div>
            <TabelaCampanhas campanhas={rel.campanhas} onAbrir={(c) => navigate(rotaCampanhaMeta(c.chave) + query())} />
          </>
        )}
      </div>
    </div>
  );
}
